import type { RegisteredComponent } from "@builder.io/sdk-react";
import dynamic from "next/dynamic";

import { IMAGE_FILE_TYPES, VIDEO_FILE_TYPES } from "@/libs/builder/consts";
import { withValidation } from "@/libs/builder/validators/validateBuilderComponentData";

export const buildVideoPlayerInputs = () => {
  return [
    {
      name: "title",
      type: "string",
    },
    {
      name: "videoType",
      friendlyName: "Hosting",
      type: "string",
      enum: ["youtube", "hosted internally"],
      defaultValue: "hosted internally",
      required: true,
    },

    // YouTube video configuration
    {
      name: "youtubeConfiguration",
      friendlyName: "YouTube Video configuration",
      type: "object",
      showIf: `options.get("videoType") === "youtube"`,
      subFields: [
        {
          name: "externalLinkVideo",
          friendlyName: "YouTube video link",
          type: "string",
          required: true,
        },
      ],
    },

    // Hosted video configuration
    {
      name: "hostedVideoConfiguration",
      friendlyName: "Hosted Video configuration",
      type: "object",
      defaultValue: {
        desktopSrc:
          "https://cdn.builder.io/o/assets%2F90293f7dc9704cb5935f357315020051%2Fdb83e0ca80d24c6da4c2fb863cd65ebb%2Fcompressed?apiKey=90293f7dc9704cb5935f357315020051&token=db83e0ca80d24c6da4c2fb863cd65ebb&alt=media&optimized=true",
      },
      showIf: `options.get("videoType") === "hosted internally"`,
      subFields: [
        {
          name: "loop",
          friendlyName: "Loop video",
          type: "boolean",
        },
        {
          name: "autoPlay",
          friendlyName: "Auto play video",
          type: "boolean",
        },
        {
          name: "desktopSrc",
          friendlyName: "Desktop video (>960px)",
          type: "file",
          allowedFileTypes: VIDEO_FILE_TYPES,
          required: true,
        },
        {
          name: "tabletSrc",
          friendlyName: "Tablet video (480px to 959px)",
          type: "file",
          allowedFileTypes: VIDEO_FILE_TYPES,
          required: true,
        },
        {
          name: "mobileSrc",
          friendlyName: "Mobile video (<479px)",
          type: "file",
          allowedFileTypes: VIDEO_FILE_TYPES,
          required: true,
        },
      ],
    },

    // Common configuration for all video types
    {
      name: "controlColor",
      friendlyName: "Control color",
      type: "string",
      enum: ["dark", "light"],
      defaultValue: "dark",
    },
    {
      name: "posterImage",
      friendlyName: "Poster image",
      type: "file",
      allowedFileTypes: IMAGE_FILE_TYPES,
    },
    {
      name: "posterAlt",
      friendlyName: "Poster image alt",
      type: "string",
      defaultValue: "",
    },
    {
      name: "description",
      friendlyName: "Description",
      type: "string",
      defaultValue: "",
    },
    {
      name: "duration",
      friendlyName: "Duration",
      type: "boolean",
    },
    {
      name: "playLabel",
      friendlyName: "Play label",
      type: "string",
      required: true,
    },
    {
      name: "replayLabel",
      friendlyName: "Replay label",
      type: "string",
      required: true,
    },
    {
      name: "seekLabel",
      friendlyName: "Seek label",
      type: "string",
      required: true,
    },
    {
      name: "muteLabel",
      friendlyName: "Mute label",
      type: "string",
      required: true,
    },
    {
      name: "soundOnLabel",
      friendlyName: "Sound on label",
      type: "string",
      required: true,
    },
    {
      name: "volumeLabel",
      friendlyName: "Volume label",
      type: "string",
      required: true,
    },
    {
      name: "showFullScreenLabel",
      friendlyName: "Show Fullscreen label",
      type: "string",
      required: true,
    },
    {
      name: "showFullscreen",
      friendlyName: "Enable fullscreen button",
      type: "boolean",
    },
    {
      name: "showControls",
      friendlyName: "Enable video controls",
      type: "boolean",
    },
    {
      name: "showControlsWhenPlaying",
      friendlyName: "Show controls when playing",
      type: "boolean",
    },
    {
      name: "disableSeek",
      friendlyName: "Disable seek",
      type: "boolean",
    },
    {
      name: "preventMainPlayButtonClick",
      friendlyName: "Prevent of main play button click",
      type: "boolean",
    },
    {
      name: "preload",
      friendlyName: "Preload the video",
      type: "boolean",
    },
    {
      name: "contentSize",
      friendlyName: "Player adjust to size of asset",
      type: "boolean",
    },
    {
      name: "feedbackEnabled",
      friendlyName: "Enable feedback",
      helperText:
        "Show the like/dislike feedback overlay when this video ends. Requires the global feedback mechanism to be enabled.",
      type: "boolean",
      defaultValue: false,
    },

    // JSON-LD / Structured Data (VideoObject schema.org)
    {
      name: "jsonLdUploadDate",
      friendlyName: "[SEO] Upload date",
      helperText: "Required for VideoObject structured data. Pick the date the video was originally published.",
      type: "date",
    },
    {
      name: "jsonLdDuration",
      friendlyName: "[SEO] Duration (ISO 8601)",
      helperText:
        "Optional. Canonical ISO 8601 duration using fixed-length units, e.g. PT1M30S. Years and months are unsupported.",
      type: "string",
    },
    {
      name: "jsonLdIneligibleRegion",
      friendlyName: "[SEO] Ineligible regions",
      helperText:
        "Optional. Comma-separated ISO 3166-1 alpha-2 country codes where the video is restricted (e.g. US, NL).",
      type: "string",
    },
  ] as const satisfies Inputs;
};

export const videoPlayerInputs = buildVideoPlayerInputs();

export const VideoPlayerComponent: RegisteredComponent = {
  name: "VideoPlayer",
  component: dynamic(async () =>
    withValidation(videoPlayerInputs, (await import("./VideoPlayer")).VideoPlayer, "VideoPlayer")
  ),
  inputs: videoPlayerInputs,
};
